import React, { useContext, useEffect, useState } from 'react';
import { getClientContractById } from 'app/api/contractApi';
import { ToastContext } from 'app/context/ToastContext';
import { Divider } from 'primereact/divider';
import './contract-page.css';
import { useParams } from 'react-router-dom';
import { ProgressSpinner } from 'primereact/progressspinner';
import { formatFromLocalDate } from 'app/shared/util/date-utils';
import { Button } from 'primereact/button';
import axios from 'axios';
import DocumentPreviewDialog from 'app/component/document-preview-dialog';

const cellLabelStyle = {
  width: '30%',
  backgroundColor: '#f5f5f5',
  verticalAlign: 'middle',
  fontWeight: 'bold',
  color: '#555',
  padding: '0.75rem',
};

const cellValueStyle = {
  backgroundColor: '#f9f9f9',
  verticalAlign: 'middle',
  color: '#444',
  padding: '0.75rem',
};

export const ContractDetailPage = () => {
  const { id } = useParams<{ id?: string }>();
  const { showSuccess, showError } = useContext(ToastContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [previewDialogVisible, setPreviewDialogVisible] = useState(false);

  useEffect(() => {
    if (id) {
      getClientContractById(id)
        .then(r => {
          setData(r.data);
        })
        .catch(e => {
          console.error(e);
          showError('Chyba', e.response?.data?.detail);
        })
        .finally(() => setLoading(false));
    }
  }, []);

  const formatStavZmluvy = stav => {
    if (!stav) return '';

    if (stav === 1) {
      return 'Nová';
    } else if (stav === 2) {
      return 'Nevznikla';
    } else if (stav === 3) {
      return 'Aktívna';
    } else if (stav === 4) {
      return 'Ukončená';
    } else if (stav === 5) {
      return 'Stornovaná';
    } else if (stav === 6) {
      return 'Prevedená';
    } else if (stav === 7) {
      return 'Mimo správu';
    }
  };

  const formatValidityYears = years => {
    if (years === 1) return '1 rok';
    if (years >= 2 && years <= 4) return `${years} roky`;
    return `${years} rokov`;
  };

  const getInsuranceEndAtLabel = contract => {
    if (contract?.validityYears == null) return 'Dátum konca poistenia';

    return `Dátum konca poistenia (${formatValidityYears(contract.validityYears)})`;
  };

  const downloadDocument = async contractDocument => {
    const response = await axios.get(`/api/contract-documents/download/${contractDocument.id}`, {
      responseType: 'blob', // Important for binary data
    });

    // Create a URL for the blob
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', contractDocument.originalFilename); // Set the original file name
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const onDownloadDocument = documentData => {
    downloadDocument(documentData);
  };

  const onPreviewDocument = documentData => {
    setPreviewDocument(documentData);
    setPreviewDialogVisible(true);
  };

  return (
    <div>
      <h1 style={{ color: 'var(--primary-color)' }}>ZÁKLADNÉ INFORMÁCIE</h1>

      <Divider className="client-divider" />
      {loading && (
        <div className="flex justify-content-center">
          <ProgressSpinner />
        </div>
      )}
      {!loading && data && (
        <>
          <div className="mb-4 contract-detail" style={{ border: `1px solid 'var(--primary-color)'` }}>
            <div style={{ backgroundColor: 'var(--primary-color)', padding: '0.5rem' }}>
              <h5 className="mb-0 text-white">ZÁKLADNÉ INFORMÁCIE</h5>
            </div>
            <table className="table mb-0" style={{ tableLayout: 'fixed' }}>
              <tbody>
                <tr>
                  <td style={cellLabelStyle}>produkt</td>
                  <td style={cellValueStyle}>{data.product?.name}</td>
                </tr>
                <tr>
                  <td style={cellLabelStyle}>číslo zmluvy</td>
                  <td style={cellValueStyle}>{data.contractNr}</td>
                </tr>
                <tr>
                  <td style={cellLabelStyle}>stav zmluvy</td>
                  <td style={cellValueStyle}>{formatStavZmluvy(data.state?.id)}</td>
                </tr>
                <tr>
                  <td style={cellLabelStyle}>dátum podpisu</td>
                  <td style={cellValueStyle}>{formatFromLocalDate(data.dateOfSignature)}</td>
                </tr>
                <tr>
                  <td style={cellLabelStyle}>dátum účinnosti</td>
                  <td style={cellValueStyle}>{formatFromLocalDate(data.insuranceStartAt)}</td>
                </tr>
                <tr>
                  <td style={cellLabelStyle}>dátum výročia</td>
                  <td style={cellValueStyle}>{formatFromLocalDate(data.datumVyrocia)}</td>
                </tr>
                <tr>
                  <td style={cellLabelStyle}>{getInsuranceEndAtLabel(data)}</td>
                  <td style={cellValueStyle}>{formatFromLocalDate(data.insuranceEndAt)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* -- SECOND BLOCK: "OSOBNÉ ÚDAJE" -- */}
          <div className="contract-detail" style={{ border: `1px solid 'var(--primary-color)'` }}>
            <div style={{ backgroundColor: 'var(--primary-color)', padding: '0.5rem' }}>
              <h5 className="mb-0 text-white">OSOBNÉ ÚDAJE</h5>
            </div>
            <table className="table mb-0" style={{ tableLayout: 'fixed' }}>
              <tbody>
                <tr>
                  <td style={cellLabelStyle}>meno a priezvisko</td>
                  <td style={cellValueStyle}>{data.name}</td>
                </tr>
                <tr>
                  <td style={cellLabelStyle}>rodné číslo</td>
                  <td style={cellValueStyle}>{data.birthNr}</td>
                </tr>
                <tr>
                  <td style={cellLabelStyle}>dátum narodenia</td>
                  <td style={cellValueStyle}>{formatFromLocalDate(data.dateOfBirth)}</td>
                </tr>
                <tr>
                  <td style={cellLabelStyle}>štátna príslušnosť</td>
                  <td style={cellValueStyle}>{data.statnaPrislusnost}</td>
                </tr>
                <tr>
                  <td style={cellLabelStyle}>IBAN</td>
                  <td style={cellValueStyle}>{data.iban}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="contract-detail" style={{ border: `1px solid 'var(--primary-color)'`, marginTop: '5px' }}>
            <div style={{ backgroundColor: 'var(--primary-color)', padding: '0.5rem' }}>
              <h5 className="mb-0 text-white">PRÍLOHY KU ZMLUVE</h5>
            </div>

            {data?.documents && data.documents.length > 0 && (
              <div className="message-attachments">
                {data.documents.map(attachment => (
                  <div key={attachment.id} className="flex align-items-center" style={{ gap: '0.5rem', padding: '0.25rem 0.75rem' }}>
                    <Button
                      icon="pi pi-eye"
                      type="button"
                      className="p-button-text p-button-sm"
                      tooltip="Náhľad"
                      onClick={() => onPreviewDocument(attachment)}
                    />
                    <a onClick={() => onDownloadDocument(attachment)} download className="attachment-link">
                      {attachment.originalFilename}
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
      <DocumentPreviewDialog
        visible={previewDialogVisible}
        onHide={() => setPreviewDialogVisible(false)}
        document={previewDocument}
      />
    </div>
  );
};

export default ContractDetailPage;
